import React, { useRef } from "react";
import _ from "lodash";
import { fromJS, Map, List } from "immutable";

import { Button, Text } from "@showitinc/showit-wc";
import styles from "./BlockSelect.module.css";
import IDUtils from "../../utils/IDUtils";
import BlockUtils from "../../utils/BlockUtils";
import UIActions from "../../actions/UIActions";
import ElementSelect from "./ElementSelect";
import SiteDesignStore from "../../stores/SiteDesignStore";
import SelectionStore from "../../stores/SelectionStore";
import EventUtils from "../../utils/EventUtils";
import uiPrefix from "../../utils/UiPrefixUtils";

import { gatherProps } from "@/utils/Merge/MergeUtils.mjs";

import AppConstants from "../../constants/AppConstants";
import DataTypes from "@/constants/DataTypes";
import { GROUP_FOCUS_MODE } from "@/constants/UIConstants";
import GroupActions from "@/actions/GroupActions";
import PROPERTY_MAPS from "@/constants/PropertyMaps";

import { processImageDrop } from "../../utils/ImageDropUtils";
import { useDrop } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";

const {
  BUTTON_TAG_NAME,
  BLOCK_PROP_PARENT_BLOCK,
  ITEM_TYPE_BLOCK,
  PANEL_SECTION_BLOCK_INFO,
  ITEM_TYPE_CANVAS_SET,
  ELEMENT_TYPE_STATES,
  BLOCK_PROP_PARENT_BLOCK_ID,
} = AppConstants;

const HOVER_THROTTLE_TIME = 500;

// todo: mode might make sense as context.
const renderChildren = ({
  blockId,
  parentId,
  parent,
  hoverId,
  layout,
  isBlockActive,
  mode,
  theme,
}) => {
  const elementOrder = parent.get("elementOrder", List());
  const offsets = gatherProps(parent, layout, PROPERTY_MAPS.ELEMENT_POSITION, false)?.toJS();

  return elementOrder.reverse().reduce((result, id) => {
    const elementId = `${parentId}.elements.${id}`;
    const element = parent.getIn(["elements", id]);

    let isHovered = false;
    let isSelected = false;
    let isEditing = false;

    if (isBlockActive) {
      const elemSelect = BlockUtils.getElementLayoutSelectionState(element, layout);

      // filters hidden elements from selection
      if (!BlockUtils.isVisibleInLayout(element, layout)) return result;

      isHovered = hoverId === IDUtils.getID(elementId, layout);
      isSelected = elemSelect.selected;
      isEditing = elemSelect.edit;
    }
    // todo: bring this back
    // else if (selected && _.isObject(elem.get("selected"))) {
    //   isHovered = true; // highlight element in other layout.
    // }

    result.push(
      <ElementSelect
        key={elementId}
        id={elementId}
        element={element}
        blockId={blockId}
        layout={layout}
        enabled={isBlockActive}
        highlight={isHovered}
        selected={isSelected}
        editing={isEditing}
        offsets={offsets}
        mode={mode}
        theme={theme}
      />
    );

    return result;
  }, []);
};

const BlockSelect = ({
  block,
  blockContainer,
  layout,
  layoutWidth,
  active,
  hoverId,
  mode,
  theme,
}) => {
  // throttle for hover callback to reduce rerenders
  const lastHover = useRef(0);

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [NativeTypes.FILE],
    drop: (item, monitor) => {
      const files = monitor.getItem().files;
      processImageDrop({ files, block, layout, active });
    },
    hover: (item, monitor) => {
      if (
        isOver &&
        SelectionStore.getBlock()?.get("id") === block.get("id") &&
        SelectionStore.hasSelectedElements()
      ) {
        UIActions.clearSelectedElements();
      }

      const now = Date.now();
      if (now - lastHover.current < HOVER_THROTTLE_TIME) return;
      lastHover.current = now;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const showDragOverlay = isOver && canDrop;

  const handleMouseOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    UIActions.itemOver(IDUtils.getID(block.get("id"), layout));
  };

  const handleMouseOut = () => {
    UIActions.itemOut();
  };

  const handleActivateBlock = (e) => {
    if (e.target.tagName === BUTTON_TAG_NAME || e.target.closest("si-button")) {
      return;
    }

    // todo: revisit this since I don't think we're using popper anymore
    EventUtils.emitPassthroughClick();
    e.preventDefault();
    e.stopPropagation();

    activateBlock();
  };

  /** Makes this the selected block, leaving any group it was focused in. */
  const activateBlock = () => {
    if (mode?.type === GROUP_FOCUS_MODE) {
      GroupActions.blurGroup();
    }

    const usingSiteBlock = BlockUtils.isUsingSiteBlock(block);

    UIActions.itemOut();
    UIActions.selectBlock({
      id: block.get("id"),
      layout,
    });

    if (usingSiteBlock) {
      const canvasSetId = block.get("canvasSet");
      const isCanvasSet = blockContainer.get("isCanvasSet");
      if (!canvasSetId || isCanvasSet) {
        UIActions.toggleBlockPropertiesSection({ name: PANEL_SECTION_BLOCK_INFO });
      }
    }
  };

  const handleSelectCanvasSet = (e, canvasSet) => {
    e.preventDefault();
    e.stopPropagation();
    UIActions.itemOut();
    UIActions.selectCanvasSet({ canvasSet });
    UIActions.addActiveItemHistory({
      id: canvasSet.get("id"),
      type: ITEM_TYPE_CANVAS_SET,
      name: canvasSet.get("name"),
    });
  };

  const handleSelectSiteBlock = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const baseBlock = block.get(BLOCK_PROP_PARENT_BLOCK);
    if (baseBlock) {
      UIActions.itemOut();
      UIActions.selectSiteBlock({ block: baseBlock, layout });
    } else {
      // This is a normal block, not a site block
      UIActions.selectBlock({
        id: block.get("id"),
        layout,
      });
    }

    UIActions.addActiveItemHistory({
      type: ITEM_TYPE_BLOCK,
    });
  };

  const handleClearSelection = (e) => {
    EventUtils.emitPassthroughClick();
    e.preventDefault();
    e.stopPropagation();

    UIActions.clearSelectedElements();

    if (mode?.type === GROUP_FOCUS_MODE) {
      GroupActions.blurGroup();
    }
  };

  const getElements = (blockElem, layout, isBlockActive) => {
    const blockId = blockElem.get("id");
    const selected = blockElem.get("selected");
    const selectedState = selected != null ? selected.get("blockState") : null;
    const isBlogState = blockElem.getIn(["wp", "stackViews"], false) === 1;
    // a touch selects an element in another block in one press, there being
    // no hover to show the block first
    const onActivate = isBlockActive || blockElem.get("inert") ? undefined : activateBlock;

    const renderers = [];

    const elements = blockElem.get("elements");
    elements.forEach((elem, idx) => {
      const type = elem.get("type");
      let isHovered = false;
      let isSelected = false;
      let isEditing = false;

      if (BlockUtils.isVisibleInLayout(elem, layout)) {
        const elementId = IDUtils.getID(blockId, "elements", idx);

        if (isBlockActive) {
          const elemSelect = BlockUtils.getElementLayoutSelectionState(elem, layout);

          isHovered = hoverId === IDUtils.getID(elementId, layout);
          isSelected = elemSelect.selected;
          isEditing = elemSelect.edit;
        } else if (selected && _.isObject(elem.get("selected"))) {
          isHovered = true; // highlight element in other layout.
        }

        const isParent = type === DataTypes.GROUP;
        const showChildren = isParent && mode?.group?.selectionId === elementId;

        !showChildren &&
          renderers.push(
            <ElementSelect
              key={elementId}
              id={elementId}
              element={elem}
              blockId={blockId}
              layout={layout}
              enabled={isBlockActive}
              onActivate={onActivate}
              highlight={isHovered}
              selected={isSelected}
              editing={isEditing}
              mode={mode}
              theme={theme}
            />
          );

        // todo: probably show these during regular show, but just select group on first click.

        showChildren &&
          mode?.group?.selectionId === elementId &&
          renderers.push(
            ...renderChildren({
              blockId,
              parent: elem,
              parentId: elementId,
              hoverId,
              layout,
              isBlockActive,
              mode,
              theme,
            })
          );
      }

      if (
        (selectedState != null && type === ELEMENT_TYPE_STATES) ||
        (isBlogState && type === ELEMENT_TYPE_STATES)
      ) {
        let stateElements = fromJS([]);

        // gather state elements
        if (isBlogState) {
          blockElem.get("states").forEach((state, idxBlock) => {
            const tempStateElements = BlockUtils.getStateElements(
              blockElem,
              `${blockId}.${ELEMENT_TYPE_STATES}.${idxBlock}`
            );
            stateElements = stateElements.concat(
              tempStateElements.map((stateElem, i) =>
                stateElem.set(
                  "overrideElementId",
                  `${blockId}.${ELEMENT_TYPE_STATES}.${idxBlock}.elements.${i}`
                )
              )
            );
          });
        } else {
          stateElements = BlockUtils.getStateElements(blockElem, selectedState);
        }

        stateElements.forEach((stateElem, stateIdx) => {
          const overrideBlogStateId = stateElem.get("overrideElementId");
          const elementId = isBlogState
            ? overrideBlogStateId
            : BlockUtils.getElementID(selectedState, stateIdx);

          if (BlockUtils.isVisibleInLayout(stateElem, layout)) {
            const elemSelect = BlockUtils.getElementLayoutSelectionState(stateElem, layout);

            const isHoveredState = hoverId === IDUtils.getID(elementId, layout);
            const isSelectedState = elemSelect.selected;
            const isEditingState = elemSelect.edit;

            let onItemSelect = null;

            if (isBlogState) {
              const statePath = overrideBlogStateId.split(".");

              onItemSelect = () => {
                UIActions.selectBlockState({
                  state: statePath.slice(0, 3).join("."),
                  statePath: statePath.slice(0, 2).join("."),
                  layout: layout,
                  blockId,
                  from: "stage",
                });
              };
            }

            const isParent = stateElem?.get("type") === DataTypes.GROUP;
            const showChildren = isParent && mode?.group?.selectionId === elementId;

            !showChildren &&
              renderers.push(
                <ElementSelect
                  blockId={blockId}
                  editing={isEditingState}
                  enabled={isBlockActive}
                  onActivate={onActivate}
                  element={stateElem}
                  highlight={isHoveredState}
                  id={elementId}
                  key={elementId}
                  layout={layout}
                  mode={mode}
                  onSelect={onItemSelect}
                  selected={isSelectedState}
                  theme={theme}
                />
              );

            showChildren &&
              renderers.push(
                ...renderChildren({
                  blockId,
                  parent: stateElem,
                  parentId: elementId,
                  hoverId,
                  layout,
                  isBlockActive,
                  mode,
                  theme,
                })
              );
          }
        });
      }
    });

    return renderers;
  };

  const inert = block.get("inert");
  const canvasSetId = block.get("canvasSet");
  const currentCanvasSet = blockContainer.get("currentCanvasSet");
  const isCanvasSet = blockContainer.get("isCanvasSet");
  const isCanvasSetBlock = currentCanvasSet && canvasSetId;
  const isPage = blockContainer.get("isPage");
  const isSiteCanvas = block.get(BLOCK_PROP_PARENT_BLOCK_ID) !== undefined;
  const baseBlock = block.get(BLOCK_PROP_PARENT_BLOCK) || block;
  const blockId = block.get("id");
  const domId = IDUtils.getDOMID("si_block", layout, blockId);
  const usingSiteBlock = block !== baseBlock;

  const height = baseBlock.getIn([layout, "h"]);
  const hovered = hoverId === IDUtils.getID(blockId, layout);

  const style = {
    position: "relative",
    width: "100%",
    height,
    minHeight: height,
    minWidth: layoutWidth,
  };
  const zIndex = block.get("z", false);

  if (zIndex) {
    style.zIndex = zIndex;
  }

  const hoverStyle = {
    position: "absolute",
    width: "100%",
    height,
    minHeight: height,
    minWidth: layoutWidth,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    WebkitAlignItems: "center",
    WebkitJustifyContent: "center",
  };

  const showHoverLayer = (!inert && !active && hovered) || (active && usingSiteBlock);

  const canvasSet = isCanvasSetBlock ? SiteDesignStore.findCanvasSet(canvasSetId) : null;

  /** @type {(e: React.SyntheticEvent) => void} */
  const doubleClickAction =
    isCanvasSetBlock && !isCanvasSet
      ? (e) => handleSelectCanvasSet(e, canvasSet)
      : handleSelectSiteBlock;

  let hoverLayer = null;
  if (showHoverLayer && !showDragOverlay) {
    const label = usingSiteBlock
      ? isCanvasSetBlock && isPage
        ? canvasSet.get("name")
        : baseBlock.get("name")
      : block.get("name");
    /** @type {(e: React.SyntheticEvent) => void} */
    const clickAction =
      isCanvasSetBlock && !isCanvasSet
        ? (e) => handleSelectCanvasSet(e, canvasSet)
        : handleSelectSiteBlock;

    const siteCanvasClass = usingSiteBlock ? styles["site-canvas"] : "";

    hoverLayer = (
      <div
        style={hoverStyle}
        data-block-id={blockId}
        className={`${styles["block-select-this"]} ${siteCanvasClass}`}
      >
        <Button
          data-testid={`block-select-button-${label}`}
          size="lg"
          iconPrefix={usingSiteBlock ? "siteCanvas" : "pageCanvas"}
          iconSize="20"
          onSIClick={clickAction}
          className={styles["block-select-button"]}
        >
          <Text className={styles["block-select-button-text"]}>{label}</Text>
        </Button>
      </div>
    );
  }

  const dragOverlay = showDragOverlay ? (
    <div data-testid="drag-overlay-container" className={styles["drag-overlay-container"]}>
      <div className={styles["drag-overlay"]}>
        <span className={"glyphicon glyphicon-circle_plus"}></span>
        Drop Image Here
      </div>
    </div>
  ) : null;

  const elements = usingSiteBlock ? null : getElements(block, layout, active);

  return (
    <div
      ref={drop}
      id={domId}
      data-block-id={blockId}
      style={style}
      data-testid={`${layout}-${
        isCanvasSetBlock && isPage ? "canvas-set" : isSiteCanvas ? "site-canvas" : "page-canvas"
      }`}
      className={uiPrefix(
        `element-container-${isCanvasSetBlock && isPage ? "canvas-set" : "site-canvas"}`
      )}
      onClick={!inert ? (active ? handleClearSelection : handleActivateBlock) : undefined}
      onDoubleClick={!inert && active && usingSiteBlock ? doubleClickAction : null}
      onMouseEnter={!active ? handleMouseOver : null}
      onMouseLeave={!active ? handleMouseOut : null}
      data-selected={active || undefined}
    >
      {hoverLayer}
      {dragOverlay}
      {elements}
    </div>
  );
};

export default BlockSelect;
